import React, { Component } from 'react';
import { graphql, createFragmentContainer } from 'react-relay';
import { Link } from 'react-router-dom';
import { pick } from 'lodash';
import moment from 'moment';

import { formatDate } from '../../utils/functions';
import {
  StoreProvider,
  ImageUploader,
  DateTimePicker,
  ErrorFlash,
  Label,
  ShareableLink,
} from '../../components';
import { updateMessage } from '../../mutations';

import Field from './Field';
import MessageDelete from './MessageDelete';
import { formatMessageableType } from './Helpers';
import 'react-datepicker/dist/react-datepicker.css';

const MESSAGEABLE_TYPE_TO_PATH = {
  offer: 'offers',
  fast_cash_offer: 'fast-cash',
  happening: 'happenings',
};

export class MessageEditView extends Component {
  state = {
    title: this.props.message.title,
    externalUrl: this.props.message.externalUrl,
    pushNotificationText: this.props.message.pushNotificationText,
    body: this.props.message.body,
    backgroundImage: null,
    endDate: moment(this.props.message.endDate),
    callToAction: this.props.message.callToAction,
    errors: null,
  };
  handleSubmit = (event) => {
    event.preventDefault();
    const { relay, message } = this.props;
    const attributes = pick(this.state, [
      'title',
      'body',
      'backgroundImage',
      'externalUrl',
      'pushNotificationText',
      'callToAction',
    ]);
    attributes.endDate = this.state.endDate.toDate();

    updateMessage(
      relay.environment,
      { id: message.id, ...attributes },
      {
        onCompleted: this.handleCompleted,
      }
    );
  };

  handleCompleted = (response) => {
    const { errors } = response.updateMessage;
    if (errors) {
      this.setState({ errors });
    } else {
      this.props.history.push('/messages/tab/active');
    }
  };

  handleChange = (name, value) => {
    this.setState({ [name]: value });
  };

  handleImageUploaded = (backgroundImage) => {
    this.setState({ backgroundImage });
  };

  handleDateChange = (endDate) => {
    this.setState({ endDate });
  };

  renderAvailability = () => {
    const { message } = this.props;
    const { errors, endDate } = this.state;

    if (message.hasMessageable) {
      return (
        <React.Fragment>
          <Label attr="endDate" text="End Date" errors={[]} />
          <span>{formatDate(message.messageDisplay?.endDate)}</span>
        </React.Fragment>
      );
    } else {
      return (
        <DateTimePicker
          name="end"
          datetime={endDate}
          onChange={this.handleDateChange}
          errors={errors}
        />
      );
    }
  };

  render() {
    const { errors } = this.state;
    const { message, lists, history } = this.props;
    const {
      messageDisplay,
      mixpanelWebhook,
      pushNotificationText,
      externalUrl,
      callToAction,
    } = message;
    const { backgroundImageUrl } = messageDisplay || message;

    return (
      <div className="message-new">
        <form className="message-form" onSubmit={this.handleSubmit}>
          <div className="form-header">
            <h2>Edit Message</h2>
            <div className="form-header-actions">
              <MessageDelete
                lists={lists}
                message={message}
                history={history}
              />
              <input id="saveMessage" type="submit" value="Save" />
              {message.hasMessageable && (
                <Link
                  to={`/${MESSAGEABLE_TYPE_TO_PATH[message.messageableType]}/${
                    message.messageableId
                  }/edit`}
                >
                  <button className="button">
                    Go To {formatMessageableType(message.messageableType)}
                  </button>
                </Link>
              )}
            </div>
          </div>
          <div className="form-fields">
            <ErrorFlash errors={errors} />
            <div className="form-group">
              <h3>General</h3>
              <div className="form-group-fields">
                <div className="form-group-column">
                  <Field
                    name="title"
                    placeholder={messageDisplay && messageDisplay.title}
                    errors={errors}
                    attributes={this.state}
                    onChange={this.handleChange}
                  />
                  <Field
                    name="pushNotificationText"
                    labelText="Push Notification Text"
                    placeholder={pushNotificationText}
                    errors={errors}
                    attributes={this.state}
                    onChange={this.handleChange}
                  />
                  <Field
                    name="body"
                    placeholder={messageDisplay && messageDisplay.body}
                    errors={errors}
                    attributes={this.state}
                    onChange={this.handleChange}
                    element="textarea"
                  />
                  <ShareableLink
                    name="mixpanelWebhook"
                    title="Mixpanel Webhook"
                    url={mixpanelWebhook}
                  />
                  <Field
                    name="externalUrl"
                    labelText="External URL"
                    placeholder={externalUrl}
                    errors={errors}
                    attributes={this.state}
                    onChange={this.handleChange}
                  />
                  <Field
                    name="callToAction"
                    labelText="Call to action"
                    placeholder={callToAction}
                    errors={errors}
                    attributes={this.state}
                    onChange={this.handleChange}
                  />
                </div>
                <div className="form-group-column">
                  <div className="input image-upload">
                    <Label attr="image" text="Image" errors={errors} />
                    <div className="image-upload-hint">
                      Image should be a .png or .jpg
                    </div>
                    <ImageUploader
                      id="backgroundImage"
                      source={backgroundImageUrl}
                      onUploaded={this.handleImageUploaded}
                      type="message"
                      cover
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <h3>Availability</h3>
              <div className="availability">{this.renderAvailability()}</div>
            </div>
          </div>
        </form>
      </div>
    );
  }
}

const Container = createFragmentContainer(MessageEditView, {
  message: graphql`
    fragment MessageEdit_message on Message {
      id
      title
      body
      endDate
      externalUrl
      hasMessageable
      backgroundImageUrl
      messageableType
      messageableId
      mixpanelWebhook
      pushNotificationText
      callToAction
      messageDisplay {
        title
        body
        endDate
        externalUrl
        backgroundImageUrl
        pushNotificationText
        callToAction
      }
      ...MessageDelete_message
    }
  `,
  lists: graphql`
    fragment MessageEdit_lists on Lists {
      ...MessageDelete_lists
    }
  `,
});

export default class MessageNew extends Component {
  render() {
    return (
      <StoreProvider
        container={Container}
        query={graphql`
          query MessageEditQuery($messageId: ID!) {
            message: node(id: $messageId) {
              ...MessageEdit_message
            }
            lists {
              ...MessageEdit_lists
            }
          }
        `}
      />
    );
  }
}
